import {
  AfterContentInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[mdwAutoScroll]',
  standalone: true,
})
export class MdwAutoScroll implements AfterContentInit, OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('lock-y-offset') public lockYOffset = 30;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('observe-attributes') public observeAttributes = 'false';
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('mdwAutoScroll') public enabled = true;

  private _isLocked = false;
  private pendingAnimationFrame?: number;
  private boundAnimationStartHandler?: () => void;
  private boundAnimationEndHandler?: () => void;
  private lastScrollPos?: number;
  private nativeElement: HTMLElement;
  private mutationObserver?: MutationObserver;
  private animatingElements: HTMLElement[] = [];

  public constructor() {
    this.nativeElement = this.element.nativeElement;

    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      this.lockYOffset += 44;
    }
  }

  /**
   * Get whether to observe attrs
   * @returns {boolean}   Whether or not to observe
   */
  public getObserveAttributes(): boolean {
    return this.observeAttributes !== '' && this.observeAttributes.toLowerCase() !== 'false';
  }

  /**
   * NgAfterContentInit Interface Method
   */
  public ngAfterContentInit(): void {
    this.mutationObserver = new MutationObserver(() => {
      if (!this._isLocked) {
        this.scrollDown();
      }
    });
    this.mutationObserver.observe(this.nativeElement, {
      childList: true,
      subtree: true,
      attributes: this.getObserveAttributes(),
    });

    this.boundAnimationStartHandler = this.animationStartHandler.bind(this) as () => void;
    this.boundAnimationEndHandler = this.animationEndHandler.bind(this) as () => void;
    this.nativeElement.addEventListener('animationstart', this.boundAnimationStartHandler);
    this.nativeElement.addEventListener('transitionstart', this.boundAnimationStartHandler);
    this.nativeElement.addEventListener('animationend', this.boundAnimationEndHandler);
    this.nativeElement.addEventListener('transitionend', this.boundAnimationEndHandler);
  }

  /**
   * NgOnDestroy Interface Method
   */
  public ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.boundAnimationStartHandler) {
      this.nativeElement.removeEventListener('animationstart', this.boundAnimationStartHandler);
      this.nativeElement.removeEventListener('transitionstart', this.boundAnimationStartHandler);
    }

    if (this.boundAnimationEndHandler) {
      this.nativeElement.removeEventListener('animationend', this.boundAnimationEndHandler);
      this.nativeElement.removeEventListener('transitionend', this.boundAnimationEndHandler);
    }

    this.animatingElements = [];
    if (this.pendingAnimationFrame) {
      window.cancelAnimationFrame(this.pendingAnimationFrame);
    }
    this.pendingAnimationFrame = undefined;
  }

  /**
   * Force a scroll-down
   */
  public forceScrollDown(): void {
    // if we're forcing the scroll position,
    // reset the "lastScrollPos" to the current scroll position
    this.lastScrollPos = this.nativeElement.scrollTop;
    this.scrollDown();
  }

  /**
   * Whether or not autoscroll is locked
   * @returns {boolean}   Lock value
   */
  public isLocked(): boolean {
    return this._isLocked;
  }

  /**
   * This method, triggered by CSS animation start events, starts watching the container on
   * animation frames if it's not already.
   * If we are supposed to auto-scroll, this will handle that on an animation frame loop, keeping it
   * at the frame rate of the browser. If we aren't supposed to auto scroll, this will fail and
   * bail out anyways.
   * @param evt   The event
   */
  private animationStartHandler(evt: TransitionEvent) {
    const target = (evt as Event).target as HTMLElement;
    // Don't consider looping animations for size change watching
    if (
      (evt.type === 'animationstart' &&
        window.getComputedStyle(target).animationIterationCount === 'infinite') ||
      this.animatingElements.includes(target)
    ) {
      return;
    }

    this.animatingElements.push(target);

    if (!this.pendingAnimationFrame && this.animatingElements.length === 1) {
      this.checkAnimatedFrame();
    }
  }

  /**
   * End a size change watching period started by the animation start handler method
   * @param evt   The event
   */
  private animationEndHandler(evt: Event) {
    this.animatingElements.splice(this.animatingElements.indexOf(evt.target as HTMLElement), 1);

    if (this.animatingElements.length === 0) {
      if (this.pendingAnimationFrame) {
        window.cancelAnimationFrame(this.pendingAnimationFrame);
      }
      this.pendingAnimationFrame = undefined;
      if (!this._isLocked) {
        this.scrollDown();
      }
    }
  }

  /**
   * Scroll down if applicable, and continue the animation loop if there are still elements
   * animating that we need to watch for
   */
  private checkAnimatedFrame() {
    if (!this._isLocked) {
      this.scrollDown();
    }

    // If elements are removed from the dom before their transitions complete, they won't fire end
    // events -- need to remove them here
    this.animatingElements = this.animatingElements.filter(el => this.nativeElement.contains(el));

    if (this.animatingElements.length) {
      this.pendingAnimationFrame = window.requestAnimationFrame(() => this.checkAnimatedFrame());
    }
  }

  /**
   * Scrolls the page down
   */
  private scrollDown(): void {
    if (!this.enabled) {
      return;
    }
    const scrollPos = this.nativeElement.scrollHeight;

    if (scrollPos !== this.lastScrollPos) {
      this.nativeElement.scrollTop = this.nativeElement.scrollHeight;
      this.lastScrollPos = scrollPos;
    }
  }

  /**
   * Handler for scroll event
   */
  @HostListener('scroll') // eslint-disable-line
  protected scrollHandler(): void {
    const scrollFromBottom =
      this.nativeElement.scrollHeight -
      this.nativeElement.scrollTop -
      this.nativeElement.clientHeight;
    this._isLocked = scrollFromBottom > this.lockYOffset;
  }
}
